<template>
  <div class="flex h-full flex-col gap-5">
    <div class="flex shrink-0 flex-row gap-4">
      <FontAwesomeIcon :icon="['fal', 'hotel']" size="2x" />
      <h2 class="capitalize-first text-xl font-medium">
        {{ $t('global.company_place') }}
      </h2>
      <div class="ml-auto">
        <WalkmanButton
          v-model="displayType"
          :options="displayTypeOptions"
          track-by="id"
          attribute-label="name"
          size="small"
        />
      </div>
    </div>
    <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
      <div v-if="availablePlaceType.includes('FUN')">
        <h3 class="capitalize-first ml-1 text-xl font-bold">
          {{ $t('attributes.site') }}
        </h3>
        <div class="grid grid-cols-2 gap-3 rounded-lg bg-gray-100 p-3">
          <button
            v-for="site in localSites"
            :key="site.id"
            class="hover:bg-primary hover:text-contrast-primary flex flex-row items-center gap-2 rounded-md border p-1.5 text-left"
            :class="[
              placeId === site.id && placeType === 'FUN'
                ? 'border-primary shadow'
                : '',
              placeId === site.id
                ? 'bg-primary text-contrast-primary'
                : 'bg-white'
            ]"
            @click="handelSelectFuneralHome(site)"
          >
            <div class="shrink-0">
              <SiteAvatar :id="site.id" size="extra-small" />
            </div>
            <div class="min-w-0 flex-1">
              <h3 class="truncate text-base font-semibold">
                {{ site.name }}
              </h3>
            </div>
          </button>
        </div>
      </div>
      <div v-if="availablePlaceType.includes('SFU')">
        <h3 class="capitalize-first text-xl font-bold">
          {{ $t('attributes.room') }}
        </h3>
        <div class="flex flex-col gap-3">
          <div
            v-for="room in localRooms"
            :key="room.id"
            class="flex flex-col gap-1.5 rounded-lg bg-gray-100 p-3"
          >
            <h4 class="text-lg leading-none font-semibold">{{ room.site }}</h4>
            <div class="grid grid-cols-2 gap-3">
              <button
                v-for="location in room.locations"
                :key="location.id"
                class="hover:bg-primary hover:text-contrast-primary flex cursor-pointer flex-row items-center gap-2 rounded-md border p-1.5 text-left"
                :class="[
                  placeId === location.id && placeType === 'SFUN'
                    ? 'border-primary'
                    : '',
                  placeId === location.id
                    ? 'bg-primary text-contrast-primary'
                    : 'bg-white'
                ]"
                @click="handelSelectRoom(location)"
              >
                <div class="shrink-0">
                  <LocationAvatar :id="location.id" size="extra-small" />
                </div>
                <h3 class="min-w-0 flex-1 truncate text-base font-semibold">
                  {{ location.name }}
                </h3>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import LocationAvatar from '@c/Avatar/LocationAvatar.vue'
import SiteAvatar from '@c/Avatar/SiteAvatar.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import JsonPretty from '@c/Utility/JsonPretty.vue'

export default {
  name: 'SelectFuneralHomePlace',
  components: {
    JsonPretty,
    WalkmanButton,
    SiteAvatar,
    LocationAvatar,
    FontAwesomeIcon
  },
  props: {
    placeModel: {
      type: Object,
      required: true
    },
    placeType: {
      type: String,
      required: true
    },
    placeId: {
      type: [String, Number],
      required: true
    },
    availablePlaceType: {
      type: Array,
      required: true
    }
  },
  emits: [
    'update:placeType',
    'update:placeId',
    'update:placeName',
    'close',
    'update:placeModel'
  ],
  data() {
    return {
      showFavorites: true,
      displayType: 'standard',
      allSitesFromCorporation: null,
      allLocationsFromCorporation: null
    }
  },
  computed: {
    ...mapGetters({
      sites: 'metadata/getSitesMetadata',
      rooms: 'metadata/getRoomsMetadata',
      funeralHomes: 'metadata/getFuneralHomesMetadata'
    }),
    hasFavorites() {
      return this.funeralHomes.filter((it) => it.favorite).length > 0
    },
    displayTypeOptions() {
      return [
        {
          id: 'favorite',
          name: this.$t('attributes.favorite'),
          gate: this.hasFavorites
        },
        {
          id: 'standard',
          name: this.$t('enums.screen.screenTemplate.standard'),
          gate: true
        },
        { id: 'all', name: this.$t('global.all'), gate: true }
      ].filter((it) => it.gate)
    },
    favoriteFuneralHomesIds() {
      return this.funeralHomes.filter((fh) => !!fh.favorite).map((fh) => fh.id)
    },
    favoriteSitesIds() {
      return this.sites
        .filter((site) => {
          return this.favoriteFuneralHomesIds.includes(site.funeral_home_id)
        })
        .map((site) => site.id)
    },
    localSites() {
      if (this.displayType === 'all') {
        return this.allSitesFromCorporation
      }
      return this.sites.when(
        this.hasFavorites && this.displayType === 'favorite',
        (sites) =>
          sites.filter((it) => {
            return this.favoriteSitesIds.includes(it.id)
          })
      )
    },
    localRooms() {
      if (this.displayType === 'all') {
        return this.allLocationsFromCorporation
      }
      return this.rooms.when(
        this.hasFavorites && this.displayType === 'favorite',
        (rooms) =>
          rooms.filter((it) => {
            return this.favoriteSitesIds.includes(it.site_id)
          })
      )
    }
  },
  watch: {
    displayType(newValue, oldValue) {
      if (
        newValue !== oldValue &&
        newValue === 'all' &&
        !this.allSitesFromCorporation
      ) {
        this.processGetAllCorporation().then((response) => {
          this.allLocationsFromCorporation = response.locations
          this.allSitesFromCorporation = response.sites
        })
      }
    }
  },
  mounted() {
    if (this.placeId !== null && !this.isInFavorites()) {
      this.displayType = 'favorite'
    }
  },
  methods: {
    ...mapActions({
      processGetAllCorporation: 'metadata/getAllCorporations'
    }),
    handelSelectFuneralHome(funeralHome) {
      this.$emit('update:placeType', 'FUN')

      if (this.placeId === funeralHome.id) {
        this.$emit('update:placeId', null)
        this.$emit('update:placeModel', {})
      } else {
        this.$emit('update:placeId', funeralHome.id)
        this.$emit('update:placeModel', funeralHome)
      }

      this.$emit('close', true)
    },
    handelSelectRoom(room) {
      this.$emit('update:placeType', 'SFU')

      if (this.placeId === room.id) {
        this.$emit('update:placeId', null)
        this.$emit('update:placeModel', {})
      } else {
        this.$emit('update:placeId', room.id)
        this.$emit('update:placeModel', room)
      }

      this.$emit('close', true)
    },
    isInFavorites() {
      // If the selected place is a site
      if (this.favoriteSitesIds.includes(this.placeId)) {
        return true
      }
      // If the selected place is a location
      const site = this.rooms.find((item) =>
        item.locations.some((loc) => loc.id === this.placeId)
      )
      return !!(site && this.favoriteSitesIds.includes(site.site_id))
    }
  }
}
</script>
